import { GoldStrategyModule } from './modules/goldStrategyModule';
import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import '../css/app.css';
import { PayrollModuleView } from './modules/payrollModule';
import { HedgingModuleView } from './modules/hedgingModule';
import { StoneXModule } from './modules/stonexModule';
import { HedgingMarketDashboardModule } from './modules/hedgingMarketDashboardModule';
import { GoldForecastModule } from './modules/goldForecastModule';
import { TOKEN_KEY, GLOBAL_FILTERS_KEY, LOCALE_KEY, SALES_DATASET_MODE_KEY, API_BASE } from './shared/constants';
import { api } from './shared/api';
import { SandboxResetButton } from './shared/SandboxResetButton';
import { formatMoney, formatNumber, formatDate } from './shared/formatting';
import type { ModuleKey } from './shared/moduleTitle';
import { moduleTitle, moduleLead, SHELL_MODULE_REGISTRY, shellNavLabel } from './shared/moduleTitle';
import { BI_MODULE_KEYS, canOpenModule, hasModuleGrant } from './shared/biModuleKeys';
import { matchesQuickSearch, QuickSearchInput } from './shared/quickSearch';
import { ModuleHelpWidget } from './shared/ModuleHelpWidget';
import { SandboxBlockedNotice } from './shared/SandboxBlockedNotice';
import {
    canOpenAdmin,
    defaultAdoCompanyIds,
    previousQuarterRangeIso,
    readStoredGlobalFilters,
    normalizeNumberArray,
} from './shared/foundationFilters';
import { buildParams } from './shared/buildQueryParams';
import { Kpi, BalanceCard, LineChart, MiniChart } from './shared/charts';
import { ModuleFilters } from './modules/moduleFilters';
import { DashboardModule } from './modules/dashboardModule';
import { LandingModule } from './modules/landingModule';
import { LandingModuleV2 } from './modules/landingModuleV2';
import { CommandCenterModule } from './modules/commandCenterModule';
import { HrModule } from './modules/hrModule';
import { AssetsModule } from './modules/assetsModule';
import { ParcelsModule } from './modules/parcelsModule';
import { PriceTriggersModule } from './modules/priceTriggersModule';
import { KioskScreen } from './modules/kioskScreen';
import { AckScreen } from './modules/ackScreen';
import { CashflowModule } from './modules/cashflowModule';
import { TreasuryModule } from './modules/treasuryModule';
import { LoansModule } from './modules/loansModule';
import { BankReadyPackModule } from './modules/bankReadyPackModule';
import { BiErrorBoundary } from './components/BiErrorBoundary';
import { DocumentationModule } from './modules/documentationModule';
import { AdminModule } from './modules/adminModule';
import { AdoModule } from './modules/adoModule';
import { MonestiqModule, MonestiqLanding } from './modules/monestiqModule';
import { MonestiqFinanceModule } from './modules/monestiqFinanceModule';
import { KnjigovodstvoModule } from './modules/knjigovodstvoModule';
import { GlAnalitikaModule } from './modules/glAnalitikaModule';
import { BilancaWizardModule } from './modules/bilancaWizardModule';
import { PentestMonitorModule } from './modules/pentestMonitorModule';
import { CfoTasksModule } from './modules/cfoTasksModule';
import { MarketingModule } from './modules/marketingModule';
import { GoldCmModule } from './modules/goldCmModule';
import { WarehousePlanGenerateModule } from './modules/warehousePlanGenerateModule';
import { WarehousePlanGenerateV2Module } from './modules/warehousePlanGenerateV2Module';
import { WarehousePlanWizardModule } from './modules/warehousePlanWizardModule';
import { WarehouseLanding, WarehouseSummaryOrdersModule } from './modules/warehouseModule';
import { WarehouseRoadmapModule } from './modules/warehouseRoadmapModule';
import { GapReportModule } from './modules/gapReportModule';
import { GapAutoModule } from './modules/gapAutoModule';
import { GoldCoverageModule } from './modules/goldCoverageModule';
import { InventoryDailyModule } from './modules/inventoryDailyModule';
import { LagerPlanModule } from './modules/lagerPlanModule';
import { InsuranceStockModule } from './modules/insuranceStockModule';
import { CoverageV2Module } from './modules/coverageV2Module';
import { CoverageV3Module } from './modules/coverageV3Module';
import { WarehouseOverviewModule } from './modules/warehouseOverviewModule';
import { RestockPrepModule } from './modules/restockPrepModule';
import { BranchRestockModule } from './modules/branchRestockModule';
import { ImportOrdersModule } from './modules/importOrdersModule';
import { TcaInvoicesModule } from './modules/tcaInvoicesModule';
import { GtmScrapModule } from './modules/gtmScrapModule';
import { GoldExposureModule } from './modules/goldExposureModule';
import { WarehouseStockModule } from './modules/warehouseStockModule';
import { PnlModule, type PnlPayload } from './modules/pnlModule';
import { PnlV2Module, type PnlV2Payload, type ComparisonMode, type CustomComparePeriod, type ExplicitCompare, periodRange } from './modules/pnlV2Module';
import { AiChatBubble } from './components/ai/AiChatBubble';
import { RegisterForm } from './components/RegisterForm';
import { ForgotPasswordForm, ResetPasswordForm } from './components/PasswordResetForms';
import { ProfileTopCard, ProfileModule } from './modules/profileModule';
import { DirectoryModule } from './modules/directoryModule';
import { PoslovniceModule } from './modules/poslovniceModule';
import { RelatedCompanySalesModule } from './modules/relatedCompanySalesModule';
import { CircularsModule } from './modules/circularsModule';
import { MailingModule } from './modules/mailingModule';
import { SopModule } from './modules/sopModule';
import { EducationModule } from './modules/educationModule';
import { TasksModule } from './modules/tasksModule';
import { TeamTasksModule } from './modules/teamTasksModule';
import { ZapisniciModule } from './modules/zapisniciModule';
import { PaymentTrafficModule } from './modules/paymentTrafficModule';
import ReconciliationModule from './modules/reconciliationModule';
import { GoldStorageModule } from './modules/goldStorageModule';
import { KomisijaModule } from './modules/komisijaModule';
import { KycModule } from './modules/kycModule';
import { HrCandidateSelectionModule } from './modules/hrCandidateSelectionModule';
import { DevTestModule } from './modules/devTestModule';
import { PaymentSelectionModule } from './modules/paymentSelectionModule';
import { PaymentBatchesModule } from './modules/paymentBatchesModule';
import { ContractsModule } from './modules/contractsModule';
import { BuybackPayoutModule } from './modules/buybackPayoutModule';
import { Otkup50Module } from './modules/otkup50Module';
import { PlatniCfModule } from './modules/platniCfModule';
import { Tasks2Module } from './modules/tasks2Module';
import { PinokioFab } from './shared/PinokioFab';
import { HrmsSidebar } from './shared/HrmsSidebar';
import { HrmsHeader } from './shared/HrmsHeader';
import { HrmsDashboard } from './shared/HrmsDashboard';
import { PresentationModule, PresentationLanding, NewslettersModule } from './modules/presentationModule';
import { BuybackModule } from './modules/buybackModule';
import { GoldCogsModule } from './modules/goldCogsModule';
import { AdoModule1b } from './modules/adoModule1b';
import { Ado1111Module } from './modules/ado1111Module';
import { Ado1112Module } from './modules/ado1112Module';
import { AdoModule1c } from './modules/adoModule1c';
import { AdoOverviewModule } from './modules/adoOverviewModule';
import { PosredniciModule } from './modules/posredniciModule';
import { UpravaPlModule } from './modules/upravaPlModule';
import { UpravaDashModule } from './modules/upravaDashModule';
import { PnlKockaModule } from './modules/pnlKockaModule';
import { PlCube24Module } from './modules/plCube24Module';
import { moduleFromPathname, prettyPathFor } from './shared/prettyModulePaths';


import type {
    SalesDatasetMode,
    User,
    BiAdminUser,
    UserAssignmentRow,
    AssignmentCompany,
    AssignmentSector,
    AssignmentDepartment,
    StructurePayload,
    LoanCorrectionRow,
    LoanCorrectionPayload,
    ImpersonationPayload,
    SalesOverview,
    SalesLifecycleYear,
    SalesPartnerRow,
    SalesOpenOfferRow,
    SalesAnalyticsPayload,
    SalesDrilldownRow,
    SalesDrilldownPayload,
    SalesReconciliationPayload,
    SalesPartnerDetailPayload,
    SalesLeadTimeRow,
    SalesLeadTimePayload,
    PayrollPayload,
    HedgingPayload,
    Company,
    FoundationPayload,
    Point,
    CashflowPayload,
    CashSummary,
    BalanceTriple,
    CompanyCash,
    AccountCash,
    CashEntry,
    LoansPayload,
    LoanAggregate,
    LoanPosition,
    LoanBucket,
    LoanGroups,
    CompanyLoanGroup,
    Filters,
} from './types/biApp';

// BIS build verzija — bumpa se uz public/sw.js CACHE_NAME pri svakom frontend deploy-u.
// Vidljiva kod korisničkog imena (uz "Osvježeno") da se odmah zna je li loadan latest bundle.
const BIS_VERSION = 'v355.224';

// Sandbox anti-cheat: moduli sa ispitnim/gradivnim sadržajem koji se NE smiju otvoriti ne-adminu na vježbalištu.
const BLOCKED_IN_SANDBOX = new Set<ModuleKey>(['circulars', 'sops', 'education', 'presentation_newsletters']);

// Namjenski KYC ulaz: hostname kyc.aurodomus.eu, ili put /chat (dms.aurodomus.eu/chat), ili put /kyc.
// Ogoljeni login vodi ravno u m.72, bez registracije/PIN-a/ostalih modula.
function isKycPortal(): boolean {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    if (host === 'kyc.aurodomus.eu') return true;
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '/chat' || path === '/kyc';
}

function App() {
    // HR kiosk način (dijeljeni uređaj za check-in PIN-om) — ?kiosk=1 zaobilazi login i prikazuje samo PIN ekran.
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('kiosk') === '1') {
        return <KioskScreen locale={localStorage.getItem(LOCALE_KEY) === 'en' ? 'en' : 'hr'} />;
    }
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ack')) {
        return <AckScreen locale={localStorage.getItem(LOCALE_KEY) === 'en' ? 'en' : 'hr'} />;
    }

    const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState<User | null>(null);
    const [foundation, setFoundation] = useState<FoundationPayload | null>(null);
    const [impersonation, setImpersonation] = useState<ImpersonationPayload>({
        active: false,
        impersonator_id: null,
        impersonator_name: null,
        impersonator_email: null,
    });
    const [error, setError] = useState('');
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [loading, setLoading] = useState(Boolean(token));
    // v347.11 — instance check JE PURE — deterministicki po hostname-u, bez useState/useEffect/fetch.
    // (useState pristup je padao kroz Vite minifier — ReferenceError u runtime-u.)

    const replaceSession = useCallback((payload: { token: string; user: User; impersonation: ImpersonationPayload }) => {
        localStorage.setItem(TOKEN_KEY, payload.token);
        // Cleanup per-user cached state — bez toga impersonacija propušta podatke iz prethodne sesije
        // (npr. dechaos_inbound_email iz localStorage ostaje vjakominov pa se prikazuje na Tonćijevom profilu).
        try { localStorage.removeItem('dechaos_inbound_email'); } catch {}
        setToken(payload.token);
        setUser(payload.user);
        setImpersonation(payload.impersonation);
        void api<FoundationPayload>('/foundation', payload.token)
            .then(setFoundation)
            .catch(() => undefined);
    }, []);

    // Sandbox (vježbalište) — globalni distinktivni vizual (magenta okvir + tint), pokriva i login ekran.
    useEffect(() => {
        if (typeof window === 'undefined' || !(window as unknown as { __BIS_SANDBOX__?: boolean }).__BIS_SANDBOX__) return;
        document.body.classList.add('bis-sandbox');
        if (!document.getElementById('bis-sandbox-style')) {
            const s = document.createElement('style');
            s.id = 'bis-sandbox-style';
            s.textContent = 'body.bis-sandbox::before{content:"";position:fixed;inset:0;pointer-events:none;z-index:9998;border:8px solid #c026d3;box-shadow:inset 0 0 0 3px #fff, inset 0 0 0 11px #c026d3;}body.bis-sandbox{background:#fdf4ff !important;}';
            document.head.appendChild(s);
        }
        return () => { document.getElementById('bis-sandbox-style')?.remove(); document.body.classList.remove('bis-sandbox'); };
    }, []);

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw.js').catch(() => undefined);
        }
    }, []);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }

        Promise.all([
            api<{ user: User; impersonation: ImpersonationPayload }>('/me', token),
            api<FoundationPayload>('/foundation', token),
        ])
            .then(([me, payload]) => {
                setUser(me.user);
                setImpersonation(me.impersonation);
                setFoundation(payload);
            })
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                setUser(null);
                setFoundation(null);
                setImpersonation({
                    active: false,
                    impersonator_id: null,
                    impersonator_name: null,
                    impersonator_email: null,
                });
            })
            .finally(() => setLoading(false));
    }, [token]);

    async function login(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setError('');
        const form = new FormData(event.currentTarget);

        try {
            const response = await api<{ token?: string; user?: User; impersonation?: ImpersonationPayload; two_factor_required?: boolean }>('/login', null, {
                method: 'POST',
                body: JSON.stringify({
                    email: form.get('email'),
                    password: form.get('password'),
                    two_factor_code: (form.get('two_factor_code') as string) || undefined,
                }),
            });
            // Lozinka točna, ali treba 2FA korak → pokaži polje za kod (bez tokena).
            if (response.two_factor_required) {
                setTwoFactorRequired(true);
                setError('Unesi 6-znamenkasti kod iz aplikacije (ili recovery kod).');
                return;
            }
            setTwoFactorRequired(false);
            localStorage.setItem(TOKEN_KEY, response.token!);
            // Prvi login ikad → AuthenticatedApp otvori profil (flag preživi /me refetch).
            if (response.user!.first_login) {
                sessionStorage.setItem('bi_first_login', '1');
            }
            setToken(response.token!);
            setUser(response.user!);
            setImpersonation(response.impersonation!);
            setLoading(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login nije uspio.');
        }
    }

    async function pinLogin(pin: string): Promise<string | null> {
        try {
            const res = await fetch(`${API_BASE}/pin-login`, {
                method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (d.error === 'locked') return `Blokirano. Pokušaj ponovno za ${d.retry_minutes} min.`;
                if (d.error === 'use_password') return d.message || 'Za ovaj račun koristi lozinku.';
                if (d.error === 'invalid') return `Pogrešan PIN. Preostalo pokušaja: ${d.attempts_left}.`;
                return 'Prijava nije uspjela.';
            }
            localStorage.setItem(TOKEN_KEY, d.token);
            setToken(d.token);
            setUser(d.user);
            setImpersonation(d.impersonation);
            setLoading(true);
            return null;
        } catch {
            return 'Greška veze. Pokušaj ponovno.';
        }
    }

    async function logout() {
        try {
            await api('/logout', token, { method: 'POST' });
        } catch {
            // Token cleanup still happens locally.
        }
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        setFoundation(null);
        setImpersonation({
            active: false,
            impersonator_id: null,
            impersonator_name: null,
            impersonator_email: null,
        });
    }

    if (loading) {
        return <main className="app-shell"><section className="panel">Učitavam BI shell...</section></main>;
    }

    if (!token || !user || !foundation) {
        return <Landing onLogin={login} onPinLogin={pinLogin} error={error} twoFactorRequired={twoFactorRequired} />;
    }

    return <AuthenticatedApp user={user} setUser={setUser} foundation={foundation} token={token} onLogout={logout} impersonation={impersonation} replaceSession={replaceSession} />;
}

function Landing({ onLogin, onPinLogin, error, twoFactorRequired = false }: { onLogin: (event: FormEvent<HTMLFormElement>) => void; onPinLogin: (pin: string) => Promise<string | null>; error: string; twoFactorRequired?: boolean }) {
    // Namjenski KYC ulaz (kyc.aurodomus.eu, /chat, /kyc): ogoljeni login, samo m72, bez registracije/PIN-a/ostalih modula.
    const kycPortal = isKycPortal();
    // Reset link iz emaila: ?reset_token=...&reset_email=...
    const resetParams = useMemo(() => {
        const p = new URLSearchParams(window.location.search);
        const token = p.get('reset_token');
        const email = p.get('reset_email');
        return token && email ? { token, email } : null;
    }, []);
    const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'reset' | 'pin'>(
        resetParams ? 'reset' : 'login',
    );
    const [pinValue, setPinValue] = useState('');
    const [pinErr, setPinErr] = useState('');
    const [pinBusy, setPinBusy] = useState(false);
    const submitPin = async () => {
        if (pinBusy || pinValue.length < 4) return;
        setPinBusy(true); setPinErr('');
        const e = await onPinLogin(pinValue);
        if (e) { setPinErr(e); setPinValue(''); }
        setPinBusy(false);
    };

    const clearResetParams = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('reset_token');
        url.searchParams.delete('reset_email');
        window.history.replaceState({}, '', url.toString());
        setMode('login');
    };

    return (
        <main className="auth-shell">
            <section className="auth-screen">
                {mode === 'login' ? (
                    <form className="card auth-card" onSubmit={onLogin}>
                        <h2>{kycPortal ? 'KYC / AML portal' : 'Interni portal'}</h2>
                        <p className="muted" style={{ marginTop: 0 }}>{kycPortal ? 'Prijava za dubinsku analizu stranke (m.72)' : 'Prijava za ovlaštene korisnike'}</p>
                        <label>Email<input name="email" type="email" autoComplete="email" required /></label>
                        <label>Lozinka<input name="password" type="password" autoComplete="current-password" required /></label>
                        {twoFactorRequired ? (
                            <label>2FA kod
                                <input name="two_factor_code" type="text" inputMode="numeric" autoComplete="one-time-code"
                                    pattern="[0-9A-Za-z\-]*" maxLength={20} placeholder="123456 ili recovery kod" autoFocus required />
                            </label>
                        ) : null}
                        <button type="submit">{twoFactorRequired ? 'Potvrdi kod' : (kycPortal ? 'Prijava' : 'Uđi u BI')}</button>
                        <button type="button" className="link-button" onClick={() => setMode('forgot')} style={{ marginTop: 6 }}>
                            Zaboravili ste lozinku?
                        </button>
                        {/* PIN i registracija su interni tokovi; namjenski KYC ulaz ih ne nudi. */}
                        {!kycPortal ? (
                            <>
                                <button type="button" className="secondary" onClick={() => setMode('pin')} style={{ marginTop: 8 }}>
                                    🔑 Prijava PIN-om
                                </button>
                                <button type="button" className="secondary" onClick={() => setMode('register')} style={{ marginTop: 8 }}>
                                    Nemaš račun? Registriraj se
                                </button>
                            </>
                        ) : null}
                        <p className="error">{error}</p>
                    </form>
                ) : null}
                {mode === 'pin' ? (
                    <form className="card auth-card" onSubmit={(e) => { e.preventDefault(); void submitPin(); }}>
                        <h2>Prijava PIN-om</h2>
                        <p className="muted" style={{ marginTop: 0 }}>Unesi svoj 5-znamenkasti PIN</p>
                        <input name="pin" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus
                            value={pinValue} onChange={(ev) => setPinValue(ev.target.value.replace(/\D/g, ''))}
                            style={{ fontSize: '1.6rem', letterSpacing: '0.4rem', textAlign: 'center' }} />
                        <button type="submit" disabled={pinBusy || pinValue.length < 4}>{pinBusy ? 'Prijava…' : 'Uđi'}</button>
                        <button type="button" className="secondary" onClick={() => { setMode('login'); setPinErr(''); setPinValue(''); }} style={{ marginTop: 8 }}>
                            ← Prijava lozinkom
                        </button>
                        <p className="error">{pinErr}</p>
                    </form>
                ) : null}
                {mode === 'register' ? <RegisterForm onBackToLogin={() => setMode('login')} /> : null}
                {mode === 'forgot' ? <ForgotPasswordForm onBackToLogin={() => setMode('login')} /> : null}
                {mode === 'reset' && resetParams ? (
                    <ResetPasswordForm token={resetParams.token} email={resetParams.email} onDone={clearResetParams} />
                ) : null}
            </section>
        </main>
    );
}

function AuthenticatedApp({
    user,
    setUser,
    foundation,
    token,
    onLogout,
    impersonation,
    replaceSession,
}: {
    user: User;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    foundation: FoundationPayload;
    token: string;
    onLogout: () => void;
    impersonation: ImpersonationPayload;
    replaceSession: (payload: { token: string; user: User; impersonation: ImpersonationPayload }) => void;
}) {
    const [locale, setLocale] = useState<'hr' | 'en'>(() => {
        const fromUser = user.profile?.locale;
        if (fromUser === 'hr' || fromUser === 'en') {
            return fromUser;
        }
        return localStorage.getItem(LOCALE_KEY) === 'en' ? 'en' : 'hr';
    });
    // Prvi login → otvori profil; korisnik ga može zatvoriti.
    const [showProfile, setShowProfile] = useState<boolean>(() => {
        if (sessionStorage.getItem('bi_first_login') === '1') {
            sessionStorage.removeItem('bi_first_login');
            return true;
        }
        return false;
    });
    const [now, setNow] = useState<Date>(() => new Date());
    const [showKiosk, setShowKiosk] = useState<boolean>(false);
    // Korisnik je zaključan na m.72 i SAMO m.72: iskljucivo vanjski racun (access_scope='kyc').
    // Namjenski hostname (kyc.aurodomus.eu, /chat, /kyc) vise NE zakljucava sam po sebi -
    // interni korisnik (npr. Vedran preko kyc.aurodomus.eu kad VPN ne radi) dobiva puni BIS,
    // a Irenin racun (scope='kyc') ostaje zakljucan gdje god se prijavi jer to vec vrijedi i na
    // backendu (RestrictExternalToKyc). Odluka 14.09.2026, vidi M72_SCOPE_AUDIT.md.
    // Kad je true: nema Izlaz gumba, nema bočne navigacije, nema drugog puta do drugog modula
    // (navigateToModule guard niže, HrmsSidebar/ModuleFilters izostavljeni, KycModule onExit=undefined).
    const kycLocked = user.access_scope === 'kyc';
    const [activeModule, setActiveModule] = useState<ModuleKey>(() => {
        if (typeof window === 'undefined') return 'dashboard';
        // Vanjski KYC korisnik (bi_users.access_scope='kyc') ide ravno u m.72, bez obzira na URL.
        if (user.access_scope === 'kyc') return 'kyc';
        const fromPath = moduleFromPathname(window.location.pathname);
        if (fromPath) return fromPath;
        // m.10.2 Bilanca wizard: poveznica ?modul=bilanca (INTEGRACIJA.md), iza logina kao i svaki modul.
        if (new URLSearchParams(window.location.search).get('modul') === 'bilanca') return 'bilanca_wizard';
        const m = new URLSearchParams(window.location.search).get('m');
        if (m && BI_MODULE_KEYS.includes(m as ModuleKey)) return m as ModuleKey;
        return 'dashboard';
    });

    // Sandbox anti-cheat: na vježbalištu ne-admin NE smije otvoriti ispitne module (da ih ne izvježba pa briljira
    // na produkciji). Admini/superadmini su izuzeti (treniraju kreiranje sadržaja). Reuse globala __BIS_SANDBOX__.
    const isSandbox = typeof window !== 'undefined' && Boolean((window as unknown as { __BIS_SANDBOX__?: boolean }).__BIS_SANDBOX__);
    const isAdminRole = user.role === 'administrator' || user.role === 'superadministrator';
    const sandboxBlocked = isSandbox && !isAdminRole && BLOCKED_IN_SANDBOX.has(activeModule);

    // m.170 — obavijesti modula; toast na loginu (samo superadmin). Prioritet: osobne (tag/thread) pa opće.
    const [notesToast, setNotesToast] = useState<{ personal: number; unread: number }>({ personal: 0, unread: 0 });
    useEffect(() => {
        if (user.role !== 'superadministrator' || !token) return;
        let alive = true;
        api<{ unread: number; personal?: number }>('/module-help/notes-unread', token)
            .then((r) => { if (alive) setNotesToast({ personal: r.personal ?? 0, unread: r.unread ?? 0 }); })
            .catch(() => { /* tiho */ });
        return () => { alive = false; };
    }, [token, user.role]);

    const changeLocale = useCallback((next: 'hr' | 'en') => {
        localStorage.setItem(LOCALE_KEY, next);
        setLocale(next);
        void api('/me/locale', token, { method: 'PATCH', body: JSON.stringify({ locale: next }) }).catch(() => undefined);
    }, [token]);
    const [dechaosInboundEmail, setDechaosInboundEmail] = useState<string | null>(() => {
        try { return localStorage.getItem('dechaos_inbound_email'); } catch { return null; }
    });
    useEffect(() => {
        if (!token || !user) return;
        const enabled = (user as any).profile?.dechaos_enabled;
        if (!enabled) { setDechaosInboundEmail(null); try { localStorage.removeItem('dechaos_inbound_email'); } catch {} return; }
        (async () => {
            try {
                const r = await fetch('/api/dechaos/identity', { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) return;
                const j = await r.json();
                if (j?.enabled && j.user?.inbound_email) {
                    setDechaosInboundEmail(j.user.inbound_email);
                    try { localStorage.setItem('dechaos_inbound_email', j.user.inbound_email); } catch {}
                }
            } catch { /* silent */ }
        })();
    }, [token, user?.id, (user as any)?.profile?.dechaos_enabled]);
    const [salesDatasetMode, setSalesDatasetMode] = useState<SalesDatasetMode>(() => {
        const stored = localStorage.getItem(SALES_DATASET_MODE_KEY);
        return stored === 'offers_first' ? 'offers_first' : 'invoice_view';
    });
    const defaultVisibleCompanyIds = useMemo(
        () => normalizeNumberArray(foundation.companies.map((company) => company.id)),
        [foundation.companies],
    );
    const [filters, setFilters] = useState<Filters>(() => {
        const firstLoadQuarter = previousQuarterRangeIso();
        const stored = readStoredGlobalFilters(foundation);
        return {
            companyIds: stored.companyIds ?? normalizeNumberArray(foundation.companies.map((company) => company.id)),
            dateFrom: stored.dateFrom ?? firstLoadQuarter.dateFrom,
            dateTo: stored.dateTo ?? firstLoadQuarter.dateTo,
            accountId: '',
            counterpartyId: '',
            loanCompanyId: '',
            page: 1,
        };
    });
    const [cashflow, setCashflow] = useState<CashflowPayload | null>(null);
    const [loans, setLoans] = useState<LoansPayload | null>(null);
    const [payroll, setPayroll] = useState<PayrollPayload | null>(null);
    const [hedging, setHedging] = useState<HedgingPayload | null>(null);
    const [sales, setSales] = useState<SalesAnalyticsPayload | null>(null);
    const [pnl, setPnl] = useState<PnlPayload | null>(null);
    const [pnlLoading, setPnlLoading] = useState(false);
    const [pnlIntercompanyMode, setPnlIntercompanyMode] = useState<'eliminate' | 'show_separately' | 'include'>('eliminate');
    // v2 (Modul 2.1)
    const [pnlV2, setPnlV2] = useState<PnlV2Payload | null>(null);
    const [pnlV2Compare, setPnlV2Compare] = useState<PnlV2Payload | null>(null);
    const [pnlV2Loading, setPnlV2Loading] = useState(false);
    const [pnlV2IcMode, setPnlV2IcMode] = useState<'include' | 'eliminate' | 'isolate'>('include');
    const [pnlV2CompareMode, setPnlV2CompareMode] = useState<ComparisonMode>('none');
    const [pnlV2CustomCompare, setPnlV2CustomCompare] = useState<CustomComparePeriod>({ date_from: '2024-01-01', date_to: '2024-12-31', label: 'custom' });
    const [pnlV2ExplicitCompare, setPnlV2ExplicitCompare] = useState<ExplicitCompare>({ current_key: '2026-Q1', compare_key: '2025-Q1' });
    const [adoBuybackSpreadPct, setAdoBuybackSpreadPct] = useState<number>(4.02);  // realan default
    const [adoSalesSpreadPct, setAdoSalesSpreadPct] = useState<number>(2.01);  // realan default
    const [error, setError] = useState('');
    const [hedgeAlert, setHedgeAlert] = useState<{ active: boolean; unhedged_count?: number; oldest_min?: number; fixed_needs_release?: number } | null>(null);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(new Date()), 1000);

        return () => window.clearInterval(timer);
    }, []);

    // Hedge operativni alert (nehedgirano >30 min u OFF modu) — polla svakih 60s, prikaže crveni hue + badge.
    useEffect(() => {
        if (!token) return;
        let alive = true;
        const fetchAlert = (): void => {
            fetch('/api/hedge/alerts', { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => { if (alive && d) setHedgeAlert(d); })
                .catch(() => undefined);
        };
        fetchAlert();
        const timer = window.setInterval(fetchAlert, 60000);

        return () => { alive = false; window.clearInterval(timer); };
    }, [token]);

    const navigateToModule = useCallback((next: ModuleKey) => {
        // m.72 lockdown: dok je kycLocked, jedini dopušteni cilj je 'kyc' — nema drugog puta
        // do drugog modula (sidebar/moduleNav i dalje pozivaju ovu funkciju, ali ona ovdje stane).
        if (kycLocked && next !== 'kyc') {
            return;
        }
        if (!canOpenModule(user, next)) {
            return;
        }
        // Očisti zaostali ?tab= prethodnog modula — inače Module Help / chat kontekst
        // postane npr. "dashboard/reservations" (tab koji u novom modulu ne postoji).
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            if (url.searchParams.has('tab')) {
                url.searchParams.delete('tab');
                window.history.replaceState({}, '', url.toString());
            }
        }
        if (next === 'cashflow' || next === 'loans') {
            setFilters((current) => ({
                ...current,
                counterpartyId: '',
                loanCompanyId: '',
                accountId: next === 'cashflow' ? current.accountId : '',
                page: 1,
            }));
        } else if (next === 'ado' || next === 'hedging') {
            // ADO i Hedging su namjerno zaključani na ADO društvo.
            setFilters((current) => ({
                ...current,
                companyIds: defaultAdoCompanyIds(foundation),
                counterpartyId: '',
                loanCompanyId: '',
                page: 1,
            }));
        } else {
            setFilters((current) => ({
                ...current,
                page: 1,
            }));
        }
        setActiveModule(next);
    }, [foundation, user, kycLocked]);

    useEffect(() => {
        // m.72 lockdown: ovaj generički safety-net (postoji od prvog commita, prije
        // KYC portala) inače tiho prebaci korisnika na bilo koji drugi dostupan modul
        // čim canOpenModule(user,'kyc') vrati false — što je upravo bio uzrok da odobren
        // m.72 korisnik na kyc.aurodomus.eu ne završi u KYC modulu. Dok je kycLocked, ovaj
        // efekt ne smije micati korisnika nikamo (render niže prikazuje "pristup odbijen"
        // ako stvarno nema dozvole).
        if (kycLocked) {
            return;
        }
        const next = BI_MODULE_KEYS.find((key) => canOpenModule(user, key));
        if (next && !canOpenModule(user, activeModule)) {
            navigateToModule(next);
        }
    }, [user, activeModule, navigateToModule, kycLocked]);

    useEffect(() => {
        if (activeModule !== 'admin') {
            const url = new URL(window.location.href);
            if (url.searchParams.has('admin')) {
                url.searchParams.delete('admin');
                window.history.replaceState({}, '', url.toString());
            }
        }
    }, [activeModule]);

    // Deep-link: pretty path (/uprava/zlato, /uprava/pl) ili ?m=<moduleKey>.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (isKycPortal()) return;
        const url = new URL(window.location.href);
        const pretty = prettyPathFor(activeModule);
        if (pretty) {
            url.pathname = pretty;
            url.searchParams.delete('m');
            window.history.replaceState({}, '', url.toString());
            return;
        }
        if (url.pathname.replace(/\/+$/, '').startsWith('/uprava')) {
            url.pathname = '/';
        }
        if (activeModule === 'dashboard') {
            if (url.searchParams.has('m')) {
                url.searchParams.delete('m');
            }
        } else if (url.searchParams.get('m') !== activeModule) {
            url.searchParams.set('m', activeModule);
        }
        window.history.replaceState({}, '', url.toString());
    }, [activeModule]);

    // Theme — primjena globalnog app_theme (gold | erp) na <body data-theme>
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const theme = (foundation.ui as any).app_theme ?? 'gold';
        if (theme === 'gold') {
            document.body.removeAttribute('data-theme');
        } else {
            document.body.setAttribute('data-theme', theme);
        }
    }, [foundation.ui]);

    // Back/Forward button — kad korisnik mijenja history, sync state s URL-om
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onPop = () => {
            const fromPath = moduleFromPathname(window.location.pathname);
            if (fromPath) {
                setActiveModule(fromPath);
                return;
            }
            const m = new URLSearchParams(window.location.search).get('m');
            if (m && BI_MODULE_KEYS.includes(m as ModuleKey)) {
                setActiveModule(m as ModuleKey);
            } else {
                setActiveModule('dashboard');
            }
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    useEffect(() => {
        localStorage.setItem(GLOBAL_FILTERS_KEY, JSON.stringify({
            companyIds: filters.companyIds,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
        }));
    }, [filters.companyIds, filters.dateFrom, filters.dateTo]);
    useEffect(() => {
        localStorage.setItem(SALES_DATASET_MODE_KEY, salesDatasetMode);
    }, [salesDatasetMode]);

    useEffect(() => {
        if (activeModule !== 'dashboard' && activeModule !== 'cashflow') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        api<CashflowPayload>(`/cashflow?${params.toString()}`, token)
            .then((payload) => {
                setCashflow(payload);
                setError('');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'Cashflow se nije učitao.'));
    }, [filters, foundation.ui.default_page_size, token, activeModule]);

    useEffect(() => {
        if (activeModule !== 'dashboard' && activeModule !== 'loans') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        if (filters.loanCompanyId) {
            params.set('company_ids', filters.loanCompanyId);
        }
        if (filters.counterpartyId) {
            params.set('counterparty_id', filters.counterpartyId);
        }
        api<LoansPayload>(`/loans?${params.toString()}`, token)
            .then((payload) => setLoans(payload))
            .catch((err) => setError(err instanceof Error ? err.message : 'Pozajmice se nisu učitale.'));
    }, [filters, foundation.ui.default_page_size, token, activeModule]);

    useEffect(() => {
        if (activeModule !== 'ado') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        params.set('dataset_mode', user.role === 'superadministrator' ? salesDatasetMode : 'invoice_view');
        api<SalesAnalyticsPayload>(`/ado?${params.toString()}`, token)
            .then((payload) => {
                setSales(payload);
                setError('');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'ADO analitika se nije učitala.'));
    }, [activeModule, filters, foundation.ui.default_page_size, salesDatasetMode, token, user.role]);

    // Legacy /pnl fetch — deaktiviran (moduli 'pnl' sad koriste v2 bi_pnl_facts engine).
    // Ostavljeno radi tipova; stari endpoint i dalje postoji za eventualan rollback.
    // useEffect(() => { ... legacy /pnl fetch ... }, [...]);

    // P&L — čita iz bi_pnl_facts
    const adoCompanyId = foundation.companies.find((c) => c.code === 'ADO')?.id;
    const isAdoSelected = adoCompanyId !== undefined && filters.companyIds.includes(adoCompanyId);
    const adoUsesGlJournal = foundation.ui.ado_pnl_source === 'gl_journal';
    useEffect(() => {
        if (activeModule !== 'pnl') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        params.set('intercompany', pnlV2IcMode);
        if (isAdoSelected && !adoUsesGlJournal) {
            params.set('ado_buyback_spread_pct', String(adoBuybackSpreadPct));
            params.set('ado_sales_spread_pct', String(adoSalesSpreadPct));
        }

        // Determine MAIN period date range — za MoM/QoQ/YoY override-amo s explicit current_key.
        let mainFrom = filters.dateFrom;
        let mainTo = filters.dateTo;
        if (pnlV2CompareMode === 'mom' || pnlV2CompareMode === 'qoq' || pnlV2CompareMode === 'yoy') {
            const cur = periodRange(pnlV2CompareMode, pnlV2ExplicitCompare.current_key);
            mainFrom = cur.from;
            mainTo = cur.to;
        }
        const mainParams = new URLSearchParams(params);
        mainParams.set('date_from', mainFrom);
        mainParams.set('date_to', mainTo);

        setPnlV2Loading(true);
        api<PnlV2Payload>(`/pnl/v2?${mainParams.toString()}`, token)
            .then((payload) => {
                setPnlV2(payload);
                setError('');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'P&L v2 modul se nije učitao.'))
            .finally(() => setPnlV2Loading(false));

        // Compare period
        if (pnlV2CompareMode !== 'none') {
            let cmpFromIso: string;
            let cmpToIso: string;
            if (pnlV2CompareMode === 'custom') {
                cmpFromIso = pnlV2CustomCompare.date_from;
                cmpToIso = pnlV2CustomCompare.date_to;
            } else {
                const cmp = periodRange(pnlV2CompareMode, pnlV2ExplicitCompare.compare_key);
                cmpFromIso = cmp.from;
                cmpToIso = cmp.to;
            }
            const cmpParams = new URLSearchParams(params);
            cmpParams.set('date_from', cmpFromIso);
            cmpParams.set('date_to', cmpToIso);
            api<PnlV2Payload>(`/pnl/v2?${cmpParams.toString()}`, token)
                .then((p) => setPnlV2Compare(p))
                .catch(() => setPnlV2Compare(null));
        } else {
            setPnlV2Compare(null);
        }
    }, [activeModule, filters, pnlV2IcMode, pnlV2CompareMode, pnlV2CustomCompare, pnlV2ExplicitCompare, token, adoBuybackSpreadPct, adoSalesSpreadPct, isAdoSelected, adoUsesGlJournal]);

    useEffect(() => {
        if (activeModule !== 'payroll') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        api<PayrollPayload>(`/payroll?${params.toString()}`, token)
            .then((payload) => {
                setPayroll(payload);
                setError('');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'Plaće se nisu učitale.'));
    }, [filters, foundation.ui.default_page_size, token, activeModule]);

    useEffect(() => {
        if (activeModule !== 'hedging') {
            return;
        }
        const params = buildParams(filters, foundation.ui.default_page_size);
        api<HedgingPayload>(`/hedging?${params.toString()}`, token)
            .then((payload) => {
                setHedging(payload);
                setError('');
            })
            .catch((err) => setError(err instanceof Error ? err.message : 'Hedging se nije učitao.'));
    }, [filters, foundation.ui.default_page_size, token, activeModule]);

    function patchFilters(patch: Partial<Filters>) {
        setFilters((current) => {
            const next: Filters = { ...current, ...patch, page: patch.page ?? 1 };
            const userClearedCompanies = Array.isArray(patch.companyIds) && patch.companyIds.length === 0;
            if (next.companyIds.length === 0 && foundation.companies.length > 0 && !userClearedCompanies) {
                next.companyIds = defaultVisibleCompanyIds;
            }

            return next;
        });
    }

    async function stopImpersonation() {
        setError('');
        try {
            const res = await api<{ token: string; user: User; impersonation: ImpersonationPayload }>('/admin/stop-impersonation', token, { method: 'POST' });
            replaceSession(res);
            setActiveModule('dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : (locale === 'en' ? 'Could not end impersonation.' : 'Ne mogu završiti impersonaciju.'));
        }
    }

    const isHrms = ((foundation.ui as unknown as { app_theme?: string }).app_theme ?? 'gold') === 'hrms';

    // m.72 lockdown, drugi dio: korisnik zaključan na kyc.aurodomus.eu (ili access_scope='kyc')
    // koji STVARNO nema dodijeljen m.72 (canOpenModule vraća false) ne smije završiti u
    // bilo kojem drugom modulu — mora vidjeti eksplicitnu poruku o odbijenom pristupu, bez
    // sidebara, bez navigacije, samo odjava. Ovo je namjerno prije glavnog return-a: sve gore
    // navedeni hookovi su već pozvani, pa ovaj rani izlaz ne krši pravilo o redoslijedu hookova.
    if (kycLocked && !canOpenModule(user, 'kyc')) {
        return (
            <main className="app-shell dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <div className="card" style={{ maxWidth: 480, textAlign: 'center', padding: '2rem' }}>
                    <h1>{locale === 'en' ? 'Access denied' : 'Pristup odbijen'}</h1>
                    <p className="muted">
                        {locale === 'en'
                            ? 'Your account does not have the KYC (m.72) module assigned. Contact the administrator.'
                            : 'Vaš račun nema dodijeljen KYC (m.72) modul. Obratite se administratoru.'}
                    </p>
                    <button type="button" className="compact" onClick={onLogout} style={{ marginTop: '1rem' }}>
                        {locale === 'en' ? 'Log out' : 'Odjava'}
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="app-shell dashboard">
            {isHrms && !kycLocked ? (
                <HrmsSidebar user={user} locale={locale} activeModule={activeModule} onNavigate={navigateToModule} />
            ) : null}
            {typeof window !== 'undefined' && (window as unknown as { __BIS_SANDBOX__?: boolean }).__BIS_SANDBOX__ ? (
                <div style={{ position: 'sticky', top: 0, zIndex: 60, marginBottom: '0.8rem', padding: '0.6rem 1rem', borderRadius: 8, color: '#fff', fontWeight: 800, textAlign: 'center', letterSpacing: '0.06em', textShadow: '0 1px 2px rgba(0,0,0,0.45)', background: 'repeating-linear-gradient(45deg, #c026d3 0, #c026d3 16px, #86198f 16px, #86198f 32px)' }}>
                    🏖️ {locale === 'en'
                        ? 'SANDBOX — TRAINING COPY · emails OFF, no real trades/payments/documents · resets every Sunday at midnight'
                        : 'SANDBOX — VJEŽBALIŠTE · mailovi UGAŠENI, nema pravih trgovina/plaćanja/dokumenata · reset svake nedjelje u ponoć'}
                </div>
            ) : null}
            {typeof window !== 'undefined' && !(window as unknown as { __BIS_SANDBOX__?: boolean }).__BIS_SANDBOX__ ? (
                <div style={{ textAlign: 'right', marginBottom: 6 }}>
                    <a href="https://sandbox.aurodomus.eu" target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '0.74rem', color: 'var(--ad-ink)', textDecoration: 'none', border: '1px solid #e6c200', borderRadius: 6, padding: '3px 9px', background: 'var(--ad-bg)', fontWeight: 600 }}>
                        🏖️ {locale === 'en' ? 'Open training sandbox' : 'Otvori vježbalište'} →
                    </a>
                    <SandboxResetButton token={token} userEmail={user.email} locale={locale} />
                </div>
            ) : null}
            {impersonation.active ? (
                <div className="card" style={{ marginBottom: '1rem', padding: '0.65rem 1rem', background: 'rgba(180, 120, 0, 0.15)', borderColor: 'rgba(180, 120, 0, 0.45)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
                        <p style={{ margin: 0 }}>
                            <strong>{locale === 'en' ? 'Impersonation' : 'Impersonacija'}:</strong>{' '}
                            {locale === 'en' ? 'You are signed in as' : 'Prijavljeni ste kao'}{' '}
                            <strong>{user.name}</strong> ({user.email}).
                            {impersonation.impersonator_name ? (
                                <span className="muted">
                                    {' '}
                                    · {locale === 'en' ? 'Started by' : 'Pokrenuo'}{' '}
                                    {impersonation.impersonator_name}
                                    {impersonation.impersonator_email ? ` <${impersonation.impersonator_email}>` : ''}
                                </span>
                            ) : null}
                        </p>
                        <button className="compact" type="button" onClick={() => void stopImpersonation()}>
                            {locale === 'en' ? 'Exit impersonation' : 'Izađi iz impersonacije'}
                        </button>
                    </div>
                </div>
            ) : null}
            {isHrms ? (
                <HrmsHeader
                    locale={locale}
                    title={moduleTitle(activeModule, locale)}
                    lead={moduleLead(activeModule, locale)}
                    user={user}
                    vpnBadge={typeof window !== 'undefined' && (window as unknown as { __BIS_INSTANCE__?: string }).__BIS_INSTANCE__ === 'vpn'}
                    onOpenProfile={() => setShowProfile(true)}
                    onLogout={onLogout}
                    onOpenKiosk={() => setShowKiosk(true)}
                    onLocaleChange={changeLocale}
                />
            ) : (
            <header className="topbar" style={Object.assign({ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr minmax(280px, 320px)', gap: 14, alignItems: 'stretch' } as React.CSSProperties, hedgeAlert?.active ? { boxShadow: '0 0 0 2px #b14040, 0 4px 18px rgba(177,64,64,0.28)' } : {})}>
                <div>
                    <img className="brand-logo" src="/aurodomus-logo.png" alt="Auro Domus" />
                    <p className="eyebrow">Auro Domus BI</p>
                    <h1>{moduleTitle(activeModule, locale)}</h1>
                    <p className="muted">{moduleLead(activeModule, locale)}</p>
                </div>
                {/* Middle column — prikaz taskova maknut iz zaglavlja 30.08. (Vedran); ostaje samo kiosk gumb */}
                <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button type="button" onClick={() => setShowKiosk(true)}
                        style={{ alignSelf: 'flex-end', padding: '12px 22px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '1rem', letterSpacing: '0.03em', background: 'linear-gradient(135deg,var(--ad-acc),var(--ad-acc-2))', color: '#2b2410', boxShadow: '0 3px 12px rgba(168,127,31,0.4)' }}>
                        🕒 {locale === 'en' ? 'TIME CLOCK' : 'EVIDENCIJA RADA'}
                    </button>
                </div>
                <ProfileTopCard
                    user={user}
                    locale={locale}
                    onLocaleChange={changeLocale}
                    onOpenProfile={() => setShowProfile(true)}
                    onLogout={onLogout}
                    nowLabel={`${locale === 'en' ? 'Refreshed' : 'Osvježeno'} ${new Date(now).toLocaleTimeString(locale === 'en' ? 'en-GB' : 'hr-HR', { hour: '2-digit', minute: '2-digit' })} · ${BIS_VERSION}`}
                    vpnBadge={typeof window !== 'undefined' && (window as unknown as { __BIS_INSTANCE__?: string }).__BIS_INSTANCE__ === 'vpn'}
                    dechaosInboundEmail={dechaosInboundEmail}
                    hedgeAlert={hedgeAlert?.active ? hedgeAlert : null}
                    onOpenHedge={() => navigateToModule('hedging')}
                />
            </header>
            )}

            {showKiosk ? (
                <div onClick={() => setShowKiosk(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,16,8,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: 'min(420px, 96vw)', maxHeight: '92vh', overflowY: 'auto', borderRadius: 18, boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
                        <KioskScreen locale={locale} embedded />
                        <button type="button" onClick={() => setShowKiosk(false)}
                            style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, width: 40, height: 40, borderRadius: 20, border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
                    </div>
                </div>
            ) : null}

            {showProfile ? (
                <ProfileModule
                    user={user}
                    token={token}
                    locale={locale}
                    onUserUpdated={(u) => setUser(u)}
                    onClose={() => setShowProfile(false)}
                />
            ) : (
            <>
            {!isHrms && !kycLocked && !(activeModule === 'dashboard' && typeof window !== 'undefined' && (new URLSearchParams(window.location.search).get('command') === '2' || localStorage.getItem('bis-command-center') === '1')) ? (
            <ModuleFilters
                foundation={foundation}
                filters={filters}
                onChange={patchFilters}
                showAccount={activeModule === 'cashflow'}
                cashflow={cashflow}
                showCounterparty={activeModule === 'loans'}
                loans={loans}
                lockToAdoOnly={activeModule === 'ado' || activeModule === 'hedging'}
                locale={locale}
                showGlobalFilters={activeModule !== 'admin' && activeModule !== 'circulars' && activeModule !== 'documentation'}
                moduleNav={{
                    activeModule,
                    onNavigate: navigateToModule,
                    user,
                }}
            />
            ) : null}
            {error ? <p className="error">{error}</p> : null}

            {activeModule === 'dashboard' ? (isHrms
                ? <HrmsDashboard token={token} locale={locale} user={user} companiesCount={foundation.companies?.length ?? 0} onNavigate={navigateToModule} onShortcutsChange={(next) => setUser((u) => (u ? { ...u, landing_shortcuts: next } : u))} />
                : (typeof window !== 'undefined' && (new URLSearchParams(window.location.search).get('command') === '2' || localStorage.getItem('bis-command-center') === '1'))
                    ? <CommandCenterModule user={user} token={token} locale={locale} foundation={foundation} cashflow={cashflow} loans={loans} filters={filters} onOpenModule={(key) => navigateToModule(key)} />
                : (typeof window !== 'undefined' && (new URLSearchParams(window.location.search).get('v2') === '1' || localStorage.getItem('bis-landing-v2') === '1'))
                    ? <LandingModuleV2 user={user} token={token} locale={locale} foundation={foundation} cashflow={cashflow} loans={loans} filters={filters} onOpenModule={(key) => navigateToModule(key)} onShortcutsChange={(next) => setUser((u) => (u ? { ...u, landing_shortcuts: next } : u))} />
                    : <LandingModule user={user} token={token} locale={locale} onOpenModule={(key) => navigateToModule(key)} onShortcutsChange={(next) => setUser((u) => (u ? { ...u, landing_shortcuts: next } : u))} />) : null}
            {activeModule === 'hr' ? <HrModule user={user} token={token} locale={locale} onNavigate={(key) => navigateToModule(key)} /> : null}
            {activeModule === 'assets' ? <AssetsModule user={user} token={token} locale={locale} onExit={() => navigateToModule('dashboard')} embedded={isHrms} /> : null}
            {activeModule === 'price_triggers' ? <PriceTriggersModule token={token} locale={locale} /> : null}
            {activeModule === 'parcels' ? <ParcelsModule token={token} locale={locale} isSuperadmin={user.role === 'superadministrator'} /> : null}
            {activeModule === 'gold_storage' ? <GoldStorageModule token={token} locale={locale} /> : null}
            {activeModule === 'komisija' ? <KomisijaModule token={token} locale={locale} /> : null}
            {activeModule === 'kyc' ? <KycModule token={token} locale={locale} user={user} onExit={kycLocked ? undefined : () => navigateToModule('dashboard')} /> : null}
            {activeModule === 'hr_candidates' ? <HrCandidateSelectionModule token={token} locale={locale} /> : null}
            {activeModule === 'hr_devtest' ? <DevTestModule token={token} locale={locale} /> : null}
            {activeModule === 'bilanca_wizard' ? (
                <BiErrorBoundary
                    moduleName="Bilanca ADO"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <BilancaWizardModule token={token} onClose={() => navigateToModule('dashboard')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'gl_analitika' ? (
                <BiErrorBoundary
                    moduleName="Analitika glavne knjige"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <GlAnalitikaModule token={token} locale={locale} onBack={() => navigateToModule('dashboard')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'knjigovodstvo' ? <KnjigovodstvoModule locale={locale} token={token} userEmail={user.email ?? ''} onBack={() => navigateToModule('dashboard')} /> : null}
            {activeModule === 'pentest_monitor' ? ((user.email ?? '').toLowerCase() === 'vjakomin@gmail.com' ? <PentestMonitorModule locale={locale} token={token} onBack={() => navigateToModule('dashboard')} /> : <p className="error">403</p>) : null}
            {activeModule === 'cfo_tasks' ? ((user.email ?? '').toLowerCase() === 'vjakomin@gmail.com' ? <CfoTasksModule locale={locale} token={token} onBack={() => navigateToModule('dashboard')} /> : <p className="error">403</p>) : null}
            {activeModule === 'poslovnice' ? <PoslovniceModule locale={locale} token={token} userEmail={user.email ?? ''} /> : null}
            {activeModule === 'related_sales' ? <RelatedCompanySalesModule locale={locale} token={token} onBack={() => navigateToModule('dashboard')} /> : null}
            {activeModule === 'cashflow' ? (
                <BiErrorBoundary moduleName="Cashflow" locale={locale === 'en' ? 'en' : 'hr'} onResetToDashboard={() => navigateToModule('dashboard')}>
                    <TreasuryModule payload={cashflow} filters={filters} foundation={foundation} onChange={patchFilters} onExit={() => navigateToModule('dashboard')} token={token} user={user} locale={locale} embedded={isHrms} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'loans' ? <LoansModule payload={loans} filters={filters} foundation={foundation} onChange={patchFilters} locale={locale} /> : null}
            {activeModule === 'payroll' ? <PayrollModule payload={payroll} locale={locale} /> : null}
            {activeModule === 'hedging' ? <HedgingModule payload={hedging} locale={locale} token={token} /> : null}
            {activeModule === 'hedging_strategy' ? <GoldStrategyModule token={token} locale={locale} onNavigate={navigateToModule} /> : null}
            {activeModule === 'hedging_stonex' ? <StoneXModule token={token} locale={locale} /> : null}
            {activeModule === 'hedging_market_dashboard' ? <HedgingMarketDashboardModule token={token} locale={locale} onExit={() => navigateToModule('hedging')} /> : null}
            {activeModule === 'gold_forecast' ? ((user.email ?? '').toLowerCase() === 'vjakomin@gmail.com' ? <GoldForecastModule token={token} locale={locale} canRun /> : <p className="error">403 — samo vlasnik</p>) : null}
            {activeModule === 'ado' ? (
                <AdoModule
                    payload={sales}
                    token={token}
                    locale={locale}
                    filters={filters}
                    datasetMode={user.role === 'superadministrator' ? salesDatasetMode : 'invoice_view'}
                    setDatasetMode={setSalesDatasetMode}
                    canToggleDataset={user.role === 'superadministrator'}
                />
                ) : null}
            {activeModule === 'pnl' ? (
                <>
                    {isAdoSelected && !adoUsesGlJournal ? (
                        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', padding: '0.65rem 0.85rem', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid rgba(217, 119, 6, 0.25)', borderRadius: 12, marginBottom: '0.8rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
                                {locale === 'en' ? 'ADO spread override' : 'ADO spread override'}:
                            </span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#92400e' }}>
                                {locale === 'en' ? 'Buyback %' : 'Buyback %'}
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={adoBuybackSpreadPct}
                                    onChange={(e) => setAdoBuybackSpreadPct(Number(e.target.value))}
                                    style={{ width: 84, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(217, 119, 6, 0.35)', fontSize: '0.9rem', textAlign: 'right' }}
                                />
                                <span>%</span>
                                <button
                                    type="button"
                                    onClick={() => setAdoBuybackSpreadPct(4.02)}
                                    style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: 'rgba(217, 119, 6, 0.1)', color: '#92400e', fontSize: '0.7rem', cursor: 'pointer' }}
                                    title="Default buyback spread"
                                >
                                    ↺ 4.02
                                </button>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#92400e' }}>
                                {locale === 'en' ? 'Sales %' : 'Prodaja %'}
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={adoSalesSpreadPct}
                                    onChange={(e) => setAdoSalesSpreadPct(Number(e.target.value))}
                                    style={{ width: 84, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(217, 119, 6, 0.35)', fontSize: '0.9rem', textAlign: 'right' }}
                                />
                                <span>%</span>
                                <button
                                    type="button"
                                    onClick={() => setAdoSalesSpreadPct(2.01)}
                                    style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: 'rgba(217, 119, 6, 0.1)', color: '#92400e', fontSize: '0.7rem', cursor: 'pointer' }}
                                    title="Default sales spread"
                                >
                                    ↺ 2.01
                                </button>
                            </label>
                            <span style={{ fontSize: '0.74rem', color: 'rgba(0,0,0,0.55)', flexBasis: '100%', marginTop: 4 }}>
                                {locale === 'en'
                                    ? 'Buyback % applies to ADO buyback revenue (17M Q1); Sales % applies to class 7 invest sale (102M Q1). Defaults are Q1 2026 tabak actuals.'
                                    : 'Buyback % se primjenjuje na ADO buyback (17M Q1); Prodaja % na klasu 7 invest sale (102M Q1). Defaultne vrijednosti su Q1 2026 tabak actuals.'}
                            </span>
                        </div>
                    ) : null}
                <PnlV2Module
                    payload={pnlV2}
                    comparePayload={pnlV2Compare}
                    loading={pnlV2Loading}
                    locale={locale}
                    intercompanyMode={pnlV2IcMode}
                    onIntercompanyModeChange={setPnlV2IcMode}
                    comparisonMode={pnlV2CompareMode}
                    onComparisonModeChange={setPnlV2CompareMode}
                    customCompare={pnlV2CustomCompare}
                    onCustomCompareChange={setPnlV2CustomCompare}
                    explicitCompare={pnlV2ExplicitCompare}
                    onExplicitCompareChange={setPnlV2ExplicitCompare}
                    token={token}
                    companyIds={filters.companyIds}
                    dateFrom={filters.dateFrom}
                    dateTo={filters.dateTo}
                />
                </>
            ) : null}
            {activeModule === 'monestiq' ? (
                <MonestiqLanding locale={locale} user={user} onNavigateSub={(sub) => navigateToModule(sub)} />
            ) : null}
            {activeModule === 'monestiq_finance' ? (
                <MonestiqFinanceModule locale={locale} token={token} onBack={() => navigateToModule('monestiq')} />
            ) : null}
            {activeModule === 'monestiq_atm' ? (
                <MonestiqModule locale={locale} token={token} companyIds={filters.companyIds} onBack={() => navigateToModule('monestiq')} />
            ) : null}
            {activeModule === 'marketing' ? (
                <MarketingModule locale={locale} token={token} filters={filters} />
            ) : null}
            {sandboxBlocked ? (
                <SandboxBlockedNotice moduleTitle={moduleTitle(activeModule, locale)} locale={locale} />
            ) : null}
            {activeModule === 'circulars' && !sandboxBlocked ? (
                <CircularsModule
                    token={token}
                    locale={locale}
                    isSuperadmin={user.role === 'superadministrator'}
                    canCreate={user.role === 'superadministrator' || Boolean((user as any).can_create_circulars)}
                    isCircularManager={user.role === 'superadministrator' || (user.email ?? '').toLowerCase() === 'iva.breulj@aurodomus.org'}
                    canManageGroups={user.role === 'superadministrator' || Boolean((user as any).can_manage_circular_groups)}
                    // Mora pratiti backend CircularsController::assertCanSeeReadReport 1:1 (role-lista +
                    // imenovane voditeljice), inace gumb postoji/nedostaje suprotno od stvarnog prava.
                    canSeeReadReport={
                        ['superadministrator', 'superadmin', 'administrator', 'admin', 'manager'].includes(user.role ?? '')
                        || ['iva.slanac@aurodomus.org', 'marijana.grubisic@aurodomus.org'].includes((user.email ?? '').toLowerCase())
                    }
                />
            ) : null}
            {activeModule === 'mailing' && token ? (
                canOpenAdmin(user)
                    ? <MailingModule token={token} locale={locale === 'en' ? 'en' : 'hr'} />
                    : <p className="muted" style={{ padding: 20, fontStyle: 'italic' }}>{locale === 'en' ? 'Campaign management is available to administrators only.' : 'Upravljanje kampanjama dostupno je samo administratorima.'}</p>
            ) : null}
            {activeModule === 'sops' && token && !sandboxBlocked ? (
                <BiErrorBoundary
                    moduleName="SOP Akademija"
                    locale={locale === 'en' ? 'en' : 'hr'}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <SopModule token={token} locale={locale === 'en' ? 'en' : 'hr'} userName={user.name ?? ''} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'education' && !sandboxBlocked ? (
                <EducationModule token={token} locale={locale} canEdit={user.role === 'administrator' || user.role === 'superadministrator'} />
            ) : null}
            {activeModule === 'tasks' || activeModule === 'team_tasks' ? (
                // v347.5 — m.11 default = m.11.1 (Team Tasks). Asana je m.11.2 (personal_asana).
                // ACL edge: user samo s m.11.2 (bez tasks/team_tasks granta) ide ravno u svoju Asanu.
                activeModule === 'tasks' && !hasModuleGrant(user, 'tasks') && !hasModuleGrant(user, 'team_tasks') && hasModuleGrant(user, 'personal_asana') ? (
                    <TasksModule
                        token={token}
                        locale={locale}
                        onBack={() => navigateToModule('dashboard')}
                        dechaosEnabled={Boolean((user as any).profile?.dechaos_enabled)}
                        onOpenTeamTasks={() => navigateToModule('tasks')}
                    />
                ) : (
                <TeamTasksModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                    onOpenAsana={() => navigateToModule('personal_asana')}
                    onOpenTasks2={canOpenModule(user, 'tasks2') ? () => navigateToModule('tasks2') : undefined}
                    embedded={isHrms}
                />
                )
            ) : null}
            {activeModule === 'personal_asana' ? (
                <TasksModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('tasks')}
                    dechaosEnabled={Boolean((user as any).profile?.dechaos_enabled)}
                    onOpenTeamTasks={() => navigateToModule('tasks')}
                />
            ) : null}
            {activeModule === 'tasks2' ? (
                <Tasks2Module
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                    onOpenTeamTasks={canOpenModule(user, 'tasks') ? () => navigateToModule('tasks') : undefined}
                    onOpenAsana={hasModuleGrant(user, 'personal_asana') ? () => navigateToModule('personal_asana') : undefined}
                    embedded={isHrms}
                />
            ) : null}
            {activeModule === 'zapisnici' ? (
                <ZapisniciModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'payment_traffic' ? (
                <PaymentTrafficModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'payment_reconciliation' ? (
                <ReconciliationModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'payment_selection' ? (
                <PaymentSelectionModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'payment_batches' ? (
                <PaymentBatchesModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'contracts' ? (
                <ContractsModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'buyback_payout' ? (
                <BuybackPayoutModule
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'otkup50' ? (
                <Otkup50Module
                    token={token}
                    locale={locale}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'platni_cf' ? (
                <PlatniCfModule
                    token={token}
                    locale={locale}
                    user={user}
                    onBack={() => navigateToModule('dashboard')}
                />
            ) : null}
            {activeModule === 'presentation' ? (
                <PresentationLanding locale={locale} user={user} onNavigateSub={(sub) => navigateToModule(sub)} />
            ) : null}
            {activeModule === 'presentation_newsletters' && !sandboxBlocked ? (
                <NewslettersModule token={token} locale={locale} onBack={() => navigateToModule('presentation')} />
            ) : null}
            {activeModule === 'presentation_customer' ? (
                <PresentationModule token={token} locale={locale} onBack={() => navigateToModule('presentation')} />
            ) : null}
            {activeModule === 'buyback' ? (
                <BuybackModule token={token} locale={locale} />
            ) : null}
            {activeModule === 'gold_cogs' ? (
                <GoldCogsModule token={token} locale={locale} />
            ) : null}
            {activeModule === 'ado_m1b' ? (
                <AdoModule1b token={token} locale={locale} onExit={() => navigateToModule('ado')} />
            ) : null}
            {activeModule === 'ado_m1c' ? (
                <AdoModule1c token={token} locale={locale} onExit={() => navigateToModule('ado')} />
            ) : null}
            {activeModule === 'ado_1111' ? (
                <Ado1111Module token={token} locale={locale} onExit={() => navigateToModule('ado')} />
            ) : null}
            {activeModule === 'uprava_pl' ? (
                <UpravaPlModule token={token} locale={locale} onExit={() => navigateToModule('dashboard')} />
            ) : null}
            {activeModule === 'pnl_kocka' ? (
                <PnlKockaModule token={token} locale={locale} companyIds={filters.companyIds} companies={foundation.companies} onFiltersChange={patchFilters} onExit={() => navigateToModule('dashboard')} />
            ) : null}
            {activeModule === 'pnl_explorer' ? (
                <PlCube24Module token={token} locale={locale} onExit={() => navigateToModule('dashboard')} />
            ) : null}
            {activeModule === 'uprava_dash' ? (
                <UpravaDashModule token={token} locale={locale} onExit={() => navigateToModule('dashboard')} />
            ) : null}
            {activeModule === 'ado_1112' ? (
                <Ado1112Module token={token} locale={locale} onExit={() => navigateToModule('ado')} />
            ) : null}
            {activeModule === 'posrednici' ? (
                <PosredniciModule token={token} locale={locale} />
            ) : null}
            {activeModule === 'ado_overview' ? (
                <AdoOverviewModule token={token} locale={locale} />
            ) : null}
            {activeModule === 'warehouse' ? (
                <WarehouseLanding locale={locale} userEmail={user.email ?? ''} user={user} onNavigateSub={(sub) => navigateToModule(sub)} />
            ) : null}
            {activeModule === 'warehouse_gold_cm' ? <GoldCmModule locale={locale} token={token} onBack={() => navigateToModule('warehouse')} onOpenPlanGenerate={() => navigateToModule('warehouse_plan_wizard')} /> : null}
            {activeModule === 'warehouse_plan_wizard' ? <WarehousePlanWizardModule locale={locale} token={token} onBack={() => navigateToModule('warehouse')} canEditDeliveryMethods={user.role === 'administrator' || user.role === 'superadministrator' || user.role === 'manager'} /> : null}
            {activeModule === 'warehouse_plan_generate' ? <WarehousePlanGenerateModule locale={locale} token={token} onBack={() => navigateToModule('warehouse')} /> : null}
            {activeModule === 'warehouse_plan_generate_v2' ? <WarehousePlanGenerateV2Module locale={locale} token={token} onBack={() => navigateToModule('warehouse')} /> : null}
            {activeModule === 'warehouse_summary_orders' ? <WarehouseSummaryOrdersModule locale={locale} token={token} isSuperadmin={user.role === 'superadministrator'} onBack={() => navigateToModule('warehouse')} /> : null}
            {activeModule === 'warehouse_stock' ? (
                <BiErrorBoundary
                    moduleName="Skladište 40.3"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <WarehouseStockModule locale={locale} token={token} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_roadmap' ? (
                <WarehouseRoadmapModule locale={locale} userEmail={user.email ?? ''} token={token} onBack={() => navigateToModule('warehouse')} />
            ) : null}
            {activeModule === 'warehouse_gap_report' ? ((user.email ?? '').toLowerCase() === 'vjakomin@gmail.com' ? <GapReportModule token={token} locale={locale} /> : <p className="error">403</p>) : null}
            {activeModule === 'warehouse_gap_auto' ? ((user.email ?? '').toLowerCase() === 'vjakomin@gmail.com' ? <GapAutoModule token={token} locale={locale} /> : <p className="error">403</p>) : null}
            {activeModule === 'warehouse_gold_coverage' ? (
                <BiErrorBoundary
                    moduleName="Pokrivenost zlatom"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <GoldCoverageModule token={token} locale={locale} isOwner={(user.email ?? '').toLowerCase() === 'vjakomin@gmail.com'} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_daily_inventory' ? <InventoryDailyModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} /> : null}
            {activeModule === 'warehouse_lager_plan' ? (
                <BiErrorBoundary
                    moduleName="Planiranje lagera"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <LagerPlanModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_insurance' ? (
                <BiErrorBoundary
                    moduleName="Osiguranje zaliha poslovnica"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <InsuranceStockModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_coverage_v2' ? (
                <BiErrorBoundary
                    moduleName="Pokrivenost zlatom V2"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <CoverageV2Module token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_coverage_v3' ? (
                <BiErrorBoundary
                    moduleName="Pokrivenost zlata V3"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <CoverageV3Module token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_overview' ? (
                <BiErrorBoundary
                    moduleName="Pregled skladišta i narudžbi"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <WarehouseOverviewModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_restock_prep' ? (
                <BiErrorBoundary
                    moduleName="Preskladištenje, priprema"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <RestockPrepModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_branch_restock' ? (
                <BiErrorBoundary
                    moduleName="Dopuna lagera, priprema"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <BranchRestockModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_import_orders' ? (
                <BiErrorBoundary
                    moduleName="Ino narudžbe i računi"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <ImportOrdersModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_tca_invoices' ? (
                <BiErrorBoundary
                    moduleName="Računi TCA"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <TcaInvoicesModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_gtm_scrap' ? (
                <BiErrorBoundary
                    moduleName="Lom GTM"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <GtmScrapModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'warehouse_exposure_v2' ? (
                <BiErrorBoundary
                    moduleName="Ukupna izlozenost zlatu"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <GoldExposureModule token={token} locale={locale} onBack={() => navigateToModule('warehouse')} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'bank_ready' ? (
                <BiErrorBoundary
                    moduleName="Bank Ready Pack"
                    locale={locale}
                    onResetToDashboard={() => navigateToModule('dashboard')}
                >
                    <BankReadyPackModule token={token} locale={locale} foundation={foundation} filters={filters} />
                </BiErrorBoundary>
            ) : null}
            {activeModule === 'documentation' ? (
                <DocumentationModule token={token} locale={locale} foundation={foundation} filters={filters} />
            ) : null}
            {activeModule === 'directory' ? (
                <DirectoryModule token={token} locale={locale} />
            ) : null}
            {activeModule === 'admin' ? (
                <AdminModule
                    token={token}
                    currentUser={user}
                    setUser={setUser}
                    locale={locale}
                    onSessionReplace={replaceSession}
                    onNavigateToCashflow={(opts) => {
                        patchFilters({
                            companyIds: opts.companyIds.length > 0 ? opts.companyIds : filters.companyIds,
                            dateFrom: opts.dateFrom ?? filters.dateFrom,
                            dateTo: opts.dateTo ?? filters.dateTo,
                        });
                        navigateToModule('cashflow');
                    }}
                />
            ) : null}
            </>
            )}
            {/* m.10.2 Bilanca wizard ima vlastite gumbe Spremi/Posalji uz rub; plutajuci BIS gumbi bi ih na mobitelu prekrili. */}
            {activeModule !== 'bilanca_wizard' ? (<>
            {/* AI asistent — otvoren svim ulogiranim korisnicima; scope (company × module) forsiran u backendu. */}
            <AiChatBubble
                token={token}
                module={activeModule}
                allowedModules={user.allowed_modules ?? []}
                userName={user.name ?? ''}
                isSuperadmin={user.role === 'superadministrator'}
                filters={{
                    company_ids: filters.companyIds,
                    date_from: filters.dateFrom,
                    date_to: filters.dateTo,
                }}
            />
            <ModuleHelpWidget activeModule={activeModule} token={token} locale={locale} isSuperadmin={user.role === 'superadministrator'} />
            {activeModule !== 'tasks2' ? (
                <PinokioFab token={token} locale={locale} onOpen={() => navigateToModule('tasks2')} />
            ) : null}
            </>) : null}
            {notesToast.personal > 0 || notesToast.unread > 0 ? (
                <div style={{ position: 'fixed', right: 20, bottom: 96, zIndex: 1250, background: '#fff', border: '1px solid', borderColor: notesToast.personal > 0 ? '#e4cfcf' : '#cfe4d3', borderLeft: `4px solid ${notesToast.personal > 0 ? '#b14040' : '#1f7a36'}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: '12px 14px', maxWidth: 320, fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span style={{ fontSize: '1.1rem' }}>{notesToast.personal > 0 ? '🔔' : '📓'}</span>
                        <div style={{ flex: 1 }}>
                            {notesToast.personal > 0 ? (
                                <strong>{notesToast.personal} {locale === 'en' ? 'for you (mentions / thread replies)' : 'za tebe (spomeni / odgovori u threadu)'}</strong>
                            ) : (
                                <strong>{notesToast.unread} {locale === 'en' ? 'new module note(s)' : 'novih bilješki u modulima'}</strong>
                            )}
                            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() => { window.location.assign('/?m=admin&admin=170'); }}
                                >
                                    {locale === 'en' ? 'Open log' : 'Otvori log'}
                                </button>
                                <button type="button" className="secondary compact" onClick={() => setNotesToast({ personal: 0, unread: 0 })}>
                                    {locale === 'en' ? 'Dismiss' : 'Odbaci'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </main>
    );
}

function PayrollModule({ payload, locale = 'hr' }: { payload: PayrollPayload | null; locale?: 'hr' | 'en' }) {
    return (
        <PayrollModuleView
            payload={payload}
            locale={locale}
            formatMoney={formatMoney}
            formatNumber={formatNumber}
            formatDate={formatDate}
            matchesQuickSearch={matchesQuickSearch}
            QuickSearchInput={QuickSearchInput}
            LineChart={LineChart}
        />
    );
}

function HedgingModule({ payload, locale = 'hr', token }: { payload: HedgingPayload | null; locale?: 'hr' | 'en'; token: string | null }) {
    return (
        <HedgingModuleView
            payload={payload}
            locale={locale}
            token={token}
            formatMoney={formatMoney}
            formatNumber={formatNumber}
            formatDate={formatDate}
            LineChart={LineChart}
        />
    );
}


createRoot(document.getElementById('root')!).render(<App />);
